import { Injectable } from "@angular/core";
import { Variables } from "src/app/core/utils/variables";
import { GuideCompletionReason } from "./guide-step.model";
import { GuideService } from "./guide.service";

type GuideStorageStatus = GuideCompletionReason;

const GUIDE_KEY_PREFIX = "guide:";

/**
 * Persiste localmente se cada trilha já foi concluída ou pulada pelo usuário logado.
 * Chave: `guide:{guideId}:{idUsuario}`. Falha silenciosa se o localStorage estiver
 * indisponível (ex.: navegação privada) — o guia continua na sessão sem persistir.
 */
@Injectable({
  providedIn: "root",
})
export class GuideStorageService {
  private listening = false;

  constructor(private readonly guideService: GuideService) {}

  /**
   * Executa um clear de storage (logout) sem apagar as marcas das trilhas.
   * Assim o balão não volta a abrir sozinho após um novo login.
   */
  static preserveDuring(clearFn: () => void): void {
    const snapshot = GuideStorageService.snapshot();
    clearFn();
    GuideStorageService.restore(snapshot);
  }

  listen(): void {
    if (this.listening) {
      return;
    }
    this.listening = true;

    this.guideService.guideCompleted$.subscribe(({ guideId, reason }) => {
      if (reason === "completed") {
        this.markCompleted(guideId);
      } else {
        this.markSkipped(guideId);
      }
    });
  }

  /** Concluída ou pulada — ambas contam para não reabrir automaticamente. */
  isCompleted(guideId: string): boolean {
    const status = this.read(guideId);
    return status === "completed" || status === "skipped";
  }

  markCompleted(guideId: string): void {
    this.write(guideId, "completed");
  }

  markSkipped(guideId: string): void {
    this.write(guideId, "skipped");
  }

  /** Limpa o estado (ponto de entrada "rever guia"). */
  reset(guideId: string): void {
    const key = this.buildKey(guideId);
    if (!key) {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage indisponível — ignora.
    }
  }

  private read(guideId: string): GuideStorageStatus | null {
    const key = this.buildKey(guideId);
    if (!key) {
      return null;
    }
    try {
      const raw = localStorage.getItem(key);
      if (raw === "completed" || raw === "skipped") {
        return raw;
      }
      return null;
    } catch {
      return null;
    }
  }

  private write(guideId: string, status: GuideStorageStatus): void {
    const key = this.buildKey(guideId);
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, status);
    } catch {
      // Storage indisponível — ignora.
    }
  }

  private buildKey(guideId: string): string | null {
    const userId = this.idUsuarioLogado();
    if (!userId || !guideId) {
      return null;
    }
    return `${GUIDE_KEY_PREFIX}${guideId}:${userId}`;
  }

  private idUsuarioLogado(): string | null {
    const user = this.usuarioLocal();
    return user?.id || user?.Id || null;
  }

  private usuarioLocal(): { id?: string; Id?: string } | null {
    try {
      const raw = localStorage.getItem(Variables.USER_DATA);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed ? parsed : null;
    } catch {
      return null;
    }
  }

  private static snapshot(): Array<[string, string]> {
    const entries: Array<[string, string]> = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(GUIDE_KEY_PREFIX)) {
          continue;
        }
        const value = localStorage.getItem(key);
        if (value != null) {
          entries.push([key, value]);
        }
      }
    } catch {
      // Storage indisponível — ignora.
    }
    return entries;
  }

  private static restore(entries: Array<[string, string]>): void {
    try {
      entries.forEach(([key, value]) => localStorage.setItem(key, value));
    } catch {
      // Storage indisponível — ignora.
    }
  }
}
